"use client"

import { useTranslations } from "next-intl"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Slider } from "@/components/ui/slider"
import { CONDITIONS, GRADE_DESCRIPTIONS } from "@/lib/constants"
import { cn } from "@/lib/utils"
import { creationFlowBlockClass, creationFlowOptionRowClass, creationFlowOptionRowSelectedClass } from "@/lib/creation-flow-form-styles"
import { unreachableCategory } from "@kraahl/contract"
import { ComicConditionStep } from "@/components/publications/creation-flow/listing-creation/steps/comic-condition-step"
import type { ListingData } from "@/types/listing"
import { CardConditionStep } from "@/components/publications/creation-flow/listing-creation/steps/card-condition-step"

interface ConditionTabProps {
  formData: {
    condition: string
    grade: number
    listingCategory?: ListingData["listingCategory"]
    cardDraft?: ListingData["cardDraft"]
    comicDraft?: ListingData["comicDraft"]
    title?: string
    description?: string
    price?: number
    quantity?: number
    currency?: string | null
    location?: string
    shipping?: ListingData["shipping"]
  }
  onFormChange: (field: string, value: unknown) => void
  needsGrading: boolean
}

export function ConditionTab({ formData, onFormChange, needsGrading }: ConditionTabProps) {
  const tLC = useTranslations("listingCreation")
  // Exhaustive category fork — a new category must pick its condition UI
  // here (the `default` probe turns a tuple widening into a compile error).
  // Merge 2026-08-04: origin's darkSheet-prop removal adopted (always
  // `compact`, panel-tone tail) on top of the comics-slice switch.
  switch (formData.listingCategory) {
    case "trading_card":
      return (
        <CardConditionStep
          data={{
            title: formData.title ?? "",
            description: formData.description ?? "",
            price: formData.price,
            quantity: formData.quantity ?? 1,
            currency: formData.currency ?? undefined,
            condition: formData.condition ?? "",
            grade: String(formData.grade ?? ""),
            shipping: formData.shipping ?? { methods: [], customOptions: [] },
            photos: [],
            listingCategory: "trading_card",
            cardDraft: formData.cardDraft,
          }}
          updateDataAction={(updates) => {
            Object.entries(updates).forEach(([field, value]) => {
              onFormChange(field, value)
            })
          }}
          onNextAction={() => {}}
          onPrevAction={() => {}}
          hideButton
          compact
        />
      )
    case "comic":
      return (
        <ComicConditionStep
          data={{
            title: formData.title ?? "",
            description: formData.description ?? "",
            price: formData.price,
            quantity: formData.quantity ?? 1,
            currency: formData.currency ?? undefined,
            condition: formData.condition ?? "",
            grade: String(formData.grade ?? ""),
            shipping: formData.shipping ?? { methods: [], customOptions: [] },
            photos: [],
            listingCategory: "comic",
            comicDraft: formData.comicDraft,
          }}
          updateDataAction={(updates) => {
            Object.entries(updates).forEach(([field, value]) => {
              onFormChange(field, value)
            })
          }}
          onNextAction={() => {}}
          onPrevAction={() => {}}
          hideButton
          compact
        />
      )
    case "lego":
    case undefined:
      break
    default:
      return unreachableCategory(formData.listingCategory)
  }

  const conditionKeys = ["new", "preOwned", "custom"] as const
  const localConditions = CONDITIONS.map((c, i) => ({
    ...c,
    label: tLC(`conditions.${conditionKeys[i]}.label`),
    description: tLC(`conditions.${conditionKeys[i]}.desc`),
  }))
  const localGrades = Object.fromEntries(
    ([1,2,3,4,5,6,7,8,9,10] as const).map(k => [k, {
      label: tLC(`grades.${k}.label`),
      description: tLC(`grades.${k}.desc`),
    }])
  ) as Record<number, { label: string; description: string }>
  const blockCls = creationFlowBlockClass
  const optionCls = cn(creationFlowOptionRowClass, "flex items-start space-x-3 cursor-pointer")
  const optionSelectedCls = creationFlowOptionRowSelectedClass
  const titleCls = "font-medium mb-3 text-foreground"
  const mutedCls = "text-sm text-muted-foreground/70 mt-2"

  return (
    <div className="space-y-6">
      <div className={cn("p-4 rounded-2xl", blockCls)}>
        <h3 className={titleCls}>{tLC("condition")}</h3>
        <RadioGroup
          value={formData.condition}
          onValueChange={(value) => onFormChange("condition", value)}
          className="space-y-2"
        >
          {localConditions.map((item) => (
            <div key={item.value}>
              <Label
                htmlFor={`condition-${item.value}`}
                className={cn(optionCls, formData.condition === item.value && optionSelectedCls)}
              >
                <RadioGroupItem
                  value={item.value}
                  id={`condition-${item.value}`}
                  className="mt-1"
                />
                <div className="space-y-1">
                  <span className="font-medium text-foreground">{item.label}</span>
                  <p className={mutedCls}>{item.description}</p>
                </div>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {needsGrading && (
        <div className={cn("p-4 rounded-2xl", blockCls)}>
          <h3 className={titleCls}>{tLC("conditionGrade")}</h3>
          <div className="space-y-6">
            <div className="px-1">
              <Slider
                tone="panel"
                value={[formData.grade]}
                min={1}
                max={10}
                step={1}
                onValueChange={(value) => onFormChange("grade", value[0])}
                className="py-3"
              />
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold text-foreground">{formData.grade}/10</div>
              <div className="font-medium mt-1 text-primary">
                {localGrades[formData.grade]?.label}
              </div>
              <p className={mutedCls}>
                {localGrades[formData.grade]?.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
